import { type AxiosError } from 'axios'
import { DevLog } from '@/helpers/consoleHelpers'
import { ConsumptionSummaryOriginEnum, LoadersEnum, SafeguardTypeEnum } from '@/enums'
import { LocationQueryValue, RouteLocationNormalized } from "vue-router"
import { useAbortController } from "@/composables/useAbortController"
import { CommonStore } from "@/stores/common"
import { getAuthenticationStore, getCommonStore, getContactStore, getContractStore, getCustomerStore, getInvoicingStore, getProductStore, getPromotionStore } from './storeHelpers'

export interface IRequiredData {
    products: boolean,
    promotion: boolean
}

export const getParameter = (param: string|string[]) => {
    return Array.isArray(param) ? param[0] : param
}

export const getParameterAsInt = (param: string|string[]) => {
    return Number.parseInt(getParameter(param), 10)
}

export const getQueryParameter = (param?: LocationQueryValue|LocationQueryValue[]): string|null => {
    return param ? (Array.isArray(param) ? param[0] : param) : null;
}

export const getQueryParameterAsInt = (param: LocationQueryValue|LocationQueryValue[]): number => {
    const getParam = getQueryParameter(param);
    if (getParam != null) {
        return Number.parseInt(getParam, 10)
    }

    return 0
}

export const getQueryParameterAsBool = (param: LocationQueryValue|LocationQueryValue[]): boolean => {
    const getParam = getQueryParameter(param);
    if (getParam != null) {
        return ["1", "true", "yes", "oui"].includes(getParam.toLowerCase())
    }

    return false
}

export const initializeRouteData = (routeDetail: RouteLocationNormalized) => {
    return initializeData('route', routeDetail, null, false)
}

export const initializeComponentData = (data: IRequiredData, refresh: boolean) => {
    return initializeData('component', null, data, refresh)
}

const initializeData = async (target: 'route'|'component', routeDetail: RouteLocationNormalized|null, data: IRequiredData|null = null, refresh: boolean) : Promise<void|undefined> => {
    const authStore = getAuthenticationStore()
    const customerStore = getCustomerStore()
    const contractStore = getContractStore()
    const contactStore = getContactStore()
    const invoicingStore = getInvoicingStore()
    const productStore = getProductStore()
    const promotionStore = getPromotionStore()
    const commonStore = getCommonStore()

    // Abort Controller initialization.
    useAbortController()

    if (commonStore && !commonStore.internetOnline) {
        // Return without refreshing for now.
        return;
    }
    if (target == 'route' && !routeDetail) {
        throw new Error("You have to pass the targeted route to get the meta")
    }
    if (authStore && authStore.isAuthenticated) {
        const dateCompare = new Date()
        dateCompare.setSeconds(dateCompare.getSeconds() - 900)
        commonStore?.startLoading(false, !refresh)
        
        return Promise.resolve(true)
            .then(() => {
                if (!customerStore) {
                    return 
                }
                if (authStore.customerId && (customerStore.id <= 0 || customerStore.id !== authStore.customerId || refresh || (customerStore.lastUpdate && new Date(customerStore.lastUpdate) < dateCompare))) {
                    commonStore?.setLoading(LoadersEnum.CUSTOMER);
                    if (customerStore.id !== authStore.customerId) {
                        contactStore?.$reset()
                        invoicingStore?.$reset()
                        contractStore?.$reset()
                        customerStore?.$reset()
                    }
                    
                    return customerStore.initCustomer(authStore.customerId)
                }

                return
            }) 
            .then(() => {
                commonStore?.setLoading(LoadersEnum.CUSTOMER, false);
                const withProducts = routeDetail?.meta.withProducts ?? (data?.products ?? false)
                if (withProducts && contractStore && productStore && contractStore.currentContract && contractStore.currentProductBaseId &&
                    (contractStore.currentProductBaseId != productStore.id || JSON.stringify(contractStore.currentOptionsSortedIds) != JSON.stringify(productStore.sortedOptionIds) || refresh || (productStore.lastUpdate && new Date(productStore.lastUpdate) < dateCompare))
                ) {
                    commonStore?.setLoading(LoadersEnum.PRODUCT);
                    return productStore.initProduct(contractStore.currentProductBaseId, contractStore.currentOptionsIds)
                }
                return
            })
            .then(() => {
                commonStore?.setLoading(LoadersEnum.PRODUCT, false);
                const withPromotion = routeDetail?.meta.withPromotion ?? (data?.promotion ?? false)
                if (withPromotion && contractStore && contractStore.currentContract?.id) {
                    let shouldInit = false
                    if (!promotionStore) {
                        shouldInit = false 
                    } else if (!promotionStore.promotions || refresh || (promotionStore.lastUpdate && new Date(promotionStore.lastUpdate) < dateCompare) || promotionStore.contractId != contractStore.currentContract?.id) {
                        shouldInit = true
                    }
                    if (shouldInit) {
                        commonStore?.setLoading(LoadersEnum.PROMOTION);
                        if (promotionStore) {
                            return promotionStore.initPromotionsFromContracts(contractStore.currentContract?.id ?? 0)
                        }
                    }
                }
                return
            })
            .catch((error: AxiosError) => {
                if (['CanceledError', 'AbortError'].includes(error.name)) {
                    DevLog.log('Request aborted by logout')
                } else {
                    console.error(error);
                }
            })
            .finally(() => {
                commonStore?.setLoading([
                    LoadersEnum.CUSTOMER, 
                    LoadersEnum.PRODUCT,
                    LoadersEnum.PROMOTION
                ], false)

                commonStore?.stopLoading()
            })
    }

    return Promise.resolve()
}

export const isConsumptionLoading = (origin?: ConsumptionSummaryOriginEnum|string|null, currentCommonStore?: CommonStore|null): boolean => {
    const commonStore = currentCommonStore ?? getCommonStore()
    if (origin) {
      const mappingOrigin: { [key: string]: string|CallableFunction|null } = {
        [ConsumptionSummaryOriginEnum.HOME]: LoadersEnum.HOME_USAGE,
        [ConsumptionSummaryOriginEnum.HOME_USAGE]: () => {
          return commonStore?.isLoadingState([
            LoadersEnum.HOME_USAGE, `${LoadersEnum.SAFEGUARD}_${SafeguardTypeEnum.OSG}`
          ]) 
        },
        [ConsumptionSummaryOriginEnum.USAGE]: null
      }
  
      const mapped = mappingOrigin[origin] ?? ''
      if (typeof mapped === 'function') {
        return mapped() 
      } else if (mapped && mapped !== '') {
        return commonStore?.isLoadingState(mapped) ?? false
      }
    }
  
    return commonStore?.requestLoading ?? false
  }